/**
 * The protein-ligand RMSD section of the MD analysis: protein backbone RMSD
 * (left axis) and ligand RMSD (right axis) against time, for one run or all
 * runs. The first section of the report; reports reuse `rmsdChartOption`.
 */
import { useMemo, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import Select from 'BMapsUI/common/Select';
import EChart from 'BMapsSrc/ui/charting/EChart';
import {
    RMSD_CHART_CAPTION, rmsdChartOption, rmsdRuns,
} from 'BMapsSrc/integrations/mdCharts';

export default function ProteinLigandRmsdChart({ compound, onChart }) {
    const theme = useTheme();
    const runs = useMemo(() => rmsdRuns(compound), [compound]);
    const [run, setRun] = useState('all');
    const textColor = theme?.palette?.text?.primary || '#444';
    const option = useMemo(
        () => rmsdChartOption(compound, { run, textColor }),
        [compound, run, textColor],
    );
    if (runs.length === 0) return null;
    return (
        <Box id={`md-rmsd-${compound.name}`} sx={{ marginTop: '0.75rem' }}>
            <Typography component="div" sx={{ fontWeight: 600 }}>
                Protein–ligand RMSD
            </Typography>
            {runs.length > 1 && (
                <Select
                    id={`md-rmsd-run-${compound.name}`}
                    label="Runs shown"
                    value={String(run)}
                    onChange={(e) => {
                        setRun(e.target.value === 'all' ? 'all' : Number(e.target.value));
                    }}
                    sx={{ width: '100%', margin: '0.25rem 0' }}
                >
                    <option value="all">All independent runs</option>
                    {runs.map((r) => <option key={r.run} value={r.run}>{`Run ${r.run}`}</option>)}
                </Select>
            )}
            <EChart
                option={option}
                height={320}
                ariaLabel={`Protein and ligand RMSD of ${compound.name}`}
                onChart={onChart}
            />
            <Typography variant="caption" component="div" sx={{ opacity: 0.8 }}>
                {RMSD_CHART_CAPTION}
            </Typography>
        </Box>
    );
}
